import { useHistory } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import DefaultCard from '../../components/default/DefaultCard';
import PageHeader from '../../components/PageHeader/PageHeader';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import useChecklistService from '../../app/services/hooks/useChecklistService';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import ChecklistDataTable from './components/tables/ChecklistDataTable';
import '../../styles/page-components.css';

interface Checklist {
    id: number;
    name: string;
    description: string;
    is_active: boolean;
    total_questions: number;
    max_possible_score: number;
    total_responses: number;
    created_at: string;
    updated_at: string;
}

interface ChecklistFilters {
    page: number;
    per_page: number;
    sort: string;
    order: string;
    search: string;
}

const breadcrumbs = [
    { label: 'Inicio', path: '/intranet' },
    { label: 'Gestión de Checklists', isCurrent: true }
];

const ChecklistsAdmin = () => {
    const history = useHistory();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { 
        getChecklists, 
        deleteChecklist,
        fetchingGetChecklists 
    } = useChecklistService();
    
    const [checklists, setChecklists] = useState<Checklist[]>([]);
    const [totalRows, setTotalRows] = useState(0);

    const [filter, setFilter] = useState<ChecklistFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc',
        search: ''
    });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const reloadTable = () => {
        getChecklists(filter, {
            onSuccess: (response: ServiceResponse) => {
                setChecklists(response.data.checklists || []);
                setTotalRows(response.data.total_rows || 0);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
            }
        });
    };

    const handleCreate = () => {
        history.push('/intranet/checklists-admin/create');
    };

    const handleEdit = (id: number) => {
        history.push(`/intranet/checklists-admin/${id}/edit`);
    };

    const handleDelete = (id: number) => {
        const checklist = checklists.find(c => c.id === id);
        
        useReactConfirmAlert().requestConfirmation({
            title: '¿Eliminar checklist?',
            message: `Está a punto de eliminar "${checklist?.name}". Esta acción no se puede deshacer.`,
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando...');
                        deleteChecklist(id, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });
                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => { return; }
                }
            }
        });
    };

    return (
        <>
            <div className="container-fluid">
                <div className="page-container">
                    <PageHeader title="" breadcrumbs={breadcrumbs} />

                    <DefaultCard>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h1 className="page-title">Gestión de Checklists</h1>
                                <p className="page-description">
                                    Crea y administra checklists para evaluar a los usuarios y generar recomendaciones personalizadas.
                                </p>
                            </div>
                            <ButtonGeneral
                                onClick={handleCreate}
                                title="Nuevo Checklist"
                                variant="primary"
                                size="small"
                                icon="fa-plus"
                                iconPosition="left"
                            />
                        </div>

                        <ChecklistDataTable
                            checklists={checklists}
                            totalRows={totalRows}
                            loading={fetchingGetChecklists}
                            edit={handleEdit}
                            destroy={handleDelete}
                            filter={filter}
                            setFilter={setFilter}
                            callbackSuccess={reloadTable}
                            onSearch={reloadTable}
                        />
                    </DefaultCard>
                </div>
            </div>
        </>
    );
};

export default ChecklistsAdmin;
